import type { LevelDefinition } from "@/levels/types";

export interface StarThresholds {
  /** Completed Final Score needed for 2 stars. */
  star2: number;
  /** Completed Final Score needed for 3 stars. */
  star3: number;
}

export interface StarThresholdValidation {
  valid: boolean;
  reason?: string;
}

/**
 * Score & Stars V3 (PR26 beta calibration - locked): the explicit Final
 * Score threshold for every shipped Campaign level, keyed by stable level
 * id. Final Score is `biteScore + skillScore + paceBonus` (see
 * `computeCampaignCompletionResult` in `./campaignCompletion`) - so RUSH,
 * geometric maneuvers, LAST-SECOND TURN V5, FLOW, TRICK/CLUTCH BITE,
 * WORMHOLE BITE/CLUTCH and Pace Bonus can all move a player across a
 * threshold; no single one of them is required. Favorable CHAOS
 * (JACKPOT/SPEED TRIAL) can help but is never the only path to mastery.
 *
 * These are fixed, authored values - deliberately never recomputed at
 * runtime from `growthNeeded`/the bite score tables. Recalibrated for
 * PR26's full scoring rework (RUSH Score V2, the post-95 Campaign speed
 * continuation, and LAST-SECOND TURN V3/V4/V5's occurrence curve, speed
 * multiplier, and re-arm gate) - the pre-PR26 Score & Stars V2 numbers
 * this table replaces are no longer accurate once that scoring is live.
 *
 * Design basis:
 * - 2 stars: "good play" - reachable through any strong legitimate
 *   scoring lane (a well-executed RUSH-heavy run, a SAFE/RUSH mix with
 *   pace, or moderate maneuver use), never requiring maneuver skill on
 *   its own.
 * - 3 stars: deliberately difficult, mastery-tier - meant to require
 *   combining multiple strengths (RUSH commitment, sustained speed,
 *   several legitimate LAST-SECOND rewards, FLOW/mixed maneuver use, good
 *   pace), but never all 15 LAST-SECONDs, genuine 75ms, farming, perfect
 *   RNG, or one single specific route.
 * - Calibrated using three real human playtest runs as anchors, weighted
 *   more heavily than the theoretical/synthetic modeling that produced
 *   earlier candidate numbers during this same recalibration effort:
 *     - OPEN FIELD: a genuine, non-farming strong attempt scored 345 ->
 *       3 stars set at 400, close enough that a somewhat-better real run
 *       reaches it, without requiring deliberately delaying completion or
 *       manufacturing repeated LAST-SECOND TURNs on this very short level.
 *     - BLUE PLANET: a good, varied run scored 647 and was explicitly
 *       judged "not worthy of 3 stars, but also not bad" -> 3 stars set
 *       at 750, clearly above that run without demanding an artificial
 *       skill-farming route.
 *     - EVENT HORIZON: the player's own existing high score is 1420 (of
 *       unverified quality - never treated as a normal-run benchmark or
 *       an Expert ceiling) on a level that plays subjectively difficult
 *       -> 3 stars set conservatively at 1500, above that high score but
 *       not an enormous unverified theoretical jump.
 * - Every other level (no direct human playtest data yet) uses the same
 *   hybrid approach - the real anchors, real growth budget/geometry, and
 *   the existing thresholds - rather than scaling everything by one
 *   ratio; later, untested levels deliberately keep their existing
 *   3-star difficulty rather than being lowered on synthetic modeling
 *   alone.
 *
 * Every shipped Campaign level must have exactly one entry here - see
 * `validateCampaignStarThresholds()`, run against every level in
 * `LevelRegistry.all()` by the Vitest suite. Daily's synthetic level
 * objects (`src/daily/convert.ts`) are never looked up here, and never
 * need an entry - Daily does not use Campaign stars.
 */
const CAMPAIGN_FINAL_SCORE_THRESHOLDS: Readonly<Record<string, StarThresholds>> = {
  "w1-l1": { star2: 150, star3: 400 }, // OPEN FIELD
  "w1-l2": { star2: 200, star3: 500 }, // GROWING UP
  "w1-l3": { star2: 200, star3: 525 }, // THE DIVIDE
  "w1-l4": { star2: 250, star3: 700 }, // CORRIDORS
  "w1-l5": { star2: 325, star3: 850 }, // THE U
  "w2-l1": { star2: 350, star3: 750 }, // BLUE PLANET
  "w2-l2": { star2: 400, star3: 825 }, // THE RIVER
  "w2-l3": { star2: 450, star3: 975 }, // CITY GRID
  "w2-l4": { star2: 500, star3: 1050 }, // FAULT LINE
  "w2-l5": { star2: 525, star3: 1150 }, // THE CORE
  "w3-l1": { star2: 600, star3: 1250 }, // LANDING SITE
  "w3-l2": { star2: 625, star3: 1375 }, // THE CANYON
  "w3-l3": { star2: 700, star3: 1475 }, // TWIN CRATERS
  "w3-l4": { star2: 700, star3: 1475 }, // TRENCH NETWORK (target 33)
  "w3-l5": { star2: 750, star3: 1650 }, // OUTPOST ALPHA (target 36)
  "w4-l1": { star2: 700, star3: 1525 }, // RING ROAD (target 34)
  "w4-l2": { star2: 725, star3: 1600 }, // THE GATE RING (target 35)
  "w4-l3": { star2: 725, star3: 1600 }, // JUNCTION (target 35)
  "w4-l4": { star2: 800, star3: 1750 }, // DOUBLE ORBIT (target 37)
  "w4-l5": { star2: 650, star3: 1500 }, // THE RELAY (target 32)
  "w5-l1": { star2: 700, star3: 1575 }, // SOLAR GATE (target 34)
  "w5-l2": { star2: 700, star3: 1600 }, // MERIDIAN (target 34)
  "w5-l3": { star2: 625, star3: 1500 }, // EVENT HORIZON (target 30)
  "w5-l4": { star2: 750, star3: 1725 }, // CORONAL WEAVE (target 36)
  "w5-l5": { star2: 650, star3: 1525 }, // APEX (target 32)
};

/**
 * This level's explicit Score & Stars V2 thresholds. Throws if the level
 * has no entry - a Campaign level shipped without one is a bug to catch
 * at authoring/test time, never a case to silently paper over with a
 * guessed or formula-derived fallback. Only ever call this with a real,
 * shipped Campaign `LevelDefinition` (i.e. one that came from
 * `LevelRegistry`) - never with a Daily/synthetic level.
 */
export function getStarThresholds(level: LevelDefinition): StarThresholds {
  const thresholds = CAMPAIGN_FINAL_SCORE_THRESHOLDS[level.id];
  if (!thresholds) {
    throw new Error(
      `No Score & Stars V2 thresholds defined for Campaign level "${level.id}" - every shipped Campaign level must have an explicit entry in CAMPAIGN_FINAL_SCORE_THRESHOLDS (src/progress/stars.ts).`,
    );
  }
  return thresholds;
}

/**
 * Stars are only meaningful for a level that was actually completed - the
 * caller decides that (this is never invoked on a game-over). Completing
 * at all always awards at least 1, regardless of Final Score (including 0
 * or negative). `finalScore` must be the completed run's full
 * `biteScore + skillScore + paceBonus` - see `computeCampaignCompletionResult()`
 * in `./campaignCompletion`, the single place this is called from on the
 * real completion path.
 */
export function computeStars(level: LevelDefinition, finalScore: number): 1 | 2 | 3 {
  const { star2, star3 } = getStarThresholds(level);
  if (finalScore >= star3) return 3;
  if (finalScore >= star2) return 2;
  return 1;
}

/**
 * Sanity-checks that every given level has a valid, explicit Score &
 * Stars V2 threshold pair (an entry exists, `star2 > 0`, `star3 > star2`).
 * Not used at runtime - a guard for level authoring/tests (see the Vitest
 * suite, which runs this against every shipped level via
 * `LevelRegistry.all()`). Never mutates or patches a bad/missing
 * threshold - a level this flags needs a real threshold decision added to
 * `CAMPAIGN_FINAL_SCORE_THRESHOLDS`, not a computed guess.
 */
export function validateCampaignStarThresholds(levels: readonly LevelDefinition[]): StarThresholdValidation {
  for (const level of levels) {
    const thresholds = CAMPAIGN_FINAL_SCORE_THRESHOLDS[level.id];
    if (!thresholds) {
      return { valid: false, reason: `${level.id} (${level.name}) has no Score & Stars V2 thresholds defined` };
    }
    if (!(thresholds.star2 > 0)) {
      return { valid: false, reason: `${level.id}: star2 threshold must be > 0, got ${thresholds.star2}` };
    }
    if (!(thresholds.star3 > thresholds.star2)) {
      return { valid: false, reason: `${level.id}: star3 threshold (${thresholds.star3}) must exceed star2 (${thresholds.star2})` };
    }
  }
  return { valid: true };
}
